import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { CheckCheck, Inbox, ShieldCheck } from "lucide-react";
import { api, ApiError, okSchema } from "@/lib/api";
import { normalizeUrgency, urgencyRank } from "@/lib/urgency";
import { PageHeader } from "@/components/layout/AppShell";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge, UrgencyBadge } from "@/components/ui/Badge";
import { Alert } from "@/components/ui/Feedback";
import { DataTable, formatDate, type Column } from "@/components/ui/DataTable";
import { useToast } from "@/components/ui/toast";
import { cn } from "@/lib/cn";

const alertSchema = z.object({
  id: z.number(),
  patient_rm: z.string(),
  patient_name: z.string().nullable().default(null),
  session_uuid: z.string().nullable().default(null),
  severity: z.string(),
  alert_code: z.string(),
  title: z.string(),
  message: z.string(),
  status: z.string(),
  acknowledged_by: z.string().nullable().default(null),
  created_at: z.string(),
});

const monitoringSchema = z.object({
  counts: z.object({
    open: z.number(),
    acknowledged: z.number(),
    resolved: z.number(),
  }),
  alerts: z.array(alertSchema).default([]),
});

type HealthAlert = z.infer<typeof alertSchema>;

const FILTERS = [
  { value: "open", label: "Terbuka" },
  { value: "acknowledged", label: "Ditangani" },
  { value: "resolved", label: "Selesai" },
  { value: "all", label: "Semua" },
] as const;

export function HealthMonitoring() {
  const [status, setStatus] = useState<string>("open");
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data, isPending, error } = useQuery({
    queryKey: ["admin", "health-monitoring", status],
    queryFn: ({ signal }) =>
      api.get(`/api/admin/health-monitoring?status=${status}`, monitoringSchema, signal),
    // Emergency alerts are time-critical; this view must not go stale silently.
    refetchInterval: 30_000,
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, next }: { id: number; next: string }) =>
      api.post(`/api/admin/health-monitoring/alerts/${id}/status`, okSchema, {
        status: next,
      }),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "health-monitoring"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "overview"] });
      toast.success({
        title:
          variables.next === "resolved" ? "Alert diselesaikan" : "Alert ditandai ditangani",
      });
    },
    onError: (cause) =>
      toast.danger({
        title: "Gagal memperbarui alert",
        description: cause instanceof ApiError ? cause.message : undefined,
      }),
  });

  // Most severe first, then newest — an operator works top-down.
  const alerts = [...(data?.alerts ?? [])].sort((a, b) => {
    const bySeverity =
      urgencyRank(normalizeUrgency(b.severity)) - urgencyRank(normalizeUrgency(a.severity));
    return bySeverity !== 0 ? bySeverity : b.created_at.localeCompare(a.created_at);
  });

  const columns: Column<HealthAlert>[] = [
    {
      key: "urgency",
      header: "Urgensi",
      width: "120px",
      render: (row) => <UrgencyBadge level={normalizeUrgency(row.severity)} />,
    },
    {
      key: "patient",
      header: "Pasien",
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate font-semibold">{row.patient_name ?? "Tanpa nama"}</p>
          <p className="text-content-subtle tabular truncate text-xs">{row.patient_rm}</p>
        </div>
      ),
    },
    {
      key: "alert",
      header: "Temuan",
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{row.title}</p>
          <p className="text-content-muted truncate text-xs">{row.message}</p>
        </div>
      ),
    },
    {
      key: "created",
      header: "Waktu",
      width: "150px",
      render: (row) => (
        <span className="text-content-muted text-xs">{formatDate(row.created_at, true)}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "130px",
      render: (row) =>
        row.status === "open" ? (
          <Badge tone="critical">Terbuka</Badge>
        ) : row.status === "acknowledged" ? (
          <Badge tone="monitor">Ditangani</Badge>
        ) : (
          <Badge tone="routine">Selesai</Badge>
        ),
    },
    {
      key: "actions",
      header: "Tindakan",
      align: "right",
      width: "190px",
      render: (row) => (
        <div className="flex justify-end gap-1.5">
          {row.status === "open" && (
            <Button
              size="sm"
              icon={<ShieldCheck className="size-3.5" />}
              loading={updateStatus.isPending && updateStatus.variables?.id === row.id}
              onClick={() => updateStatus.mutate({ id: row.id, next: "acknowledged" })}
            >
              Tangani
            </Button>
          )}
          {row.status !== "resolved" && (
            <Button
              size="sm"
              variant={row.status === "acknowledged" ? "primary" : "ghost"}
              icon={<CheckCheck className="size-3.5" />}
              loading={updateStatus.isPending && updateStatus.variables?.id === row.id}
              onClick={() => updateStatus.mutate({ id: row.id, next: "resolved" })}
            >
              Selesai
            </Button>
          )}
        </div>
      ),
    },
  ];

  const criticalCount = alerts.filter(
    (item) => normalizeUrgency(item.severity) === "critical" && item.status === "open",
  ).length;

  return (
    <>
      <PageHeader
        eyebrow="Pusat monitoring"
        title="Monitoring Kesehatan"
        description="Alert dari pemeriksaan mandiri pasien yang menembus ambang klinis."
      />

      {error && (
        <Alert tone="danger" title="Gagal memuat alert" className="mb-5">
          {error instanceof ApiError ? error.message : "Terjadi kesalahan tak terduga."}
        </Alert>
      )}

      {criticalCount > 0 && (
        <Alert
          tone="danger"
          title={`${criticalCount} pasien dengan tanda bahaya belum ditangani`}
          className="mb-5"
        >
          Hubungi pasien atau arahkan ke IGD sebelum menandai alert sebagai selesai.
        </Alert>
      )}

      <div
        role="tablist"
        aria-label="Filter status alert"
        className="border-hairline bg-surface mb-4 inline-flex gap-1 rounded-(--radius-control) border p-1"
      >
        {FILTERS.map((filter) => {
          const active = status === filter.value;
          const count =
            filter.value === "all"
              ? undefined
              : data?.counts[filter.value as keyof typeof data.counts];
          return (
            <button
              key={filter.value}
              role="tab"
              aria-selected={active}
              onClick={() => setStatus(filter.value)}
              className={cn(
                "rounded-(--radius-control) px-3.5 py-1.5 text-sm font-semibold transition-colors",
                active
                  ? "bg-accent text-accent-content"
                  : "text-content-muted hover:text-content",
              )}
            >
              {filter.label}
              {count != null && count > 0 && (
                <span className="tabular ml-1.5 opacity-70">{count}</span>
              )}
            </button>
          );
        })}
      </div>

      <Card>
        <DataTable
          columns={columns}
          rows={alerts}
          rowKey={(row) => row.id}
          isPending={isPending}
          empty={{
            icon: <Inbox className="size-5" />,
            title: "Tidak ada alert pada filter ini",
            description: "Semua pemeriksaan pasien berada dalam batas aman.",
          }}
        />
      </Card>
    </>
  );
}
